import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BehaviorSubject, EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, mergeMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {DocumentService} from '../document/document.service';
import { getEnterpriseStationIdFromUser } from '../enterprise-context';
import { DocumentListLoadingService } from '../document-list-loading.service';

export interface Quote {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
}

export interface ClientOrder {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
}

export interface BonLivraison {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
}

export interface Facture {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
}

export interface BonSortie {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
}

@Injectable({providedIn: 'root'})
export class DataService {

    private apiBase = `${environment.document_url}api/documents/statut`;
    private stationId = 0;
    private typeMap: { [key: string]: string } = {
        'devis': '7',
        'commande-client': '8',
        'bon-sortie': '9',
        'bon-livraison': '10',
        'facture': '11'

    };

    private statusMap: { [key: string]: string } = {
        'Tous': '0',
        'Brouillon': '1',
        'Accepté': '3',
        'Payé': '5',
        'Payée': '5',
        'Annulé': '6',
        'Annulée': '6',
        'Négociation': '8',
        'Crée': '9',
        'Valide': '10',
        'Livré': '11',
        'Non Payé': '12',
        'Non payé': '12',
        'Fermé': '13',
        'Reçu': '14',
        'Partiellement payé': '15',
        'Non utilisé': '19',
        'Partiellement utilisé': '20',
        'Utilisé': '18'
    };

    /** Statuts réellement utilisés par type (évite ~15 requêtes inutiles sur l’onglet « Tous »). */
    private readonly documentStatusesByType: Record<string, string[]> = {
        devis: ['Brouillon', 'Crée', 'Annulé', 'Négociation', 'Accepté'],
        'commande-client': ['Brouillon', 'Valide', 'Crée', 'Annulé'],
        facture: ['Brouillon', 'Payé', 'Non payé', 'Partiellement payé', 'Annulé'],
        avoir: ['Brouillon', 'Non utilisé', 'Partiellement utilisé', 'Utilisé', 'Annulé'],
        'bon-livraison': ['Brouillon', 'Crée', 'Livré', 'Annulé'],
        'bon-sortie': ['Brouillon', 'Crée', 'Annulé', 'Fermé'],
        'commande-fournisseur': ['Brouillon', 'Valide', 'Crée', 'Annulé'],
        'bon-reception': ['Brouillon', 'Crée', 'Annulé', 'Reçu'],
        'facture-fournisseur': ['Brouillon', 'Crée', 'Payé', 'Non payé', 'Annulé'],
    };

    /** total_items renvoyés par l'API GET api/documents/statut/{statusId}/{typeId} par statut (facture uniquement). */
    private factureTotalItemsByStatus: Record<string, number> = {};
    private factureTotalsSubject = new BehaviorSubject<Record<string, number>>({});
    /** Observable pour rafraîchir les badges d’onglets Facture. */
    factureTotals$ = this.factureTotalsSubject.asObservable();
    /** total_items par statut pour devis (api/documents/statut/{statusId}/7). */
    private quoteTotalItemsByStatus: Record<string, number> = {};

    /** Filtre calendrier actif (date_from / date_to sur l’API documents). */
    private activeDateFrom?: string;
    private activeDateTo?: string;
    private quoteTableData = new BehaviorSubject<Quote[]>([]);
    private orderTableData = new BehaviorSubject<ClientOrder[]>([]);
    private bonLivraisonTableData = new BehaviorSubject<BonLivraison[]>([]);
    private factureTableData = new BehaviorSubject<Facture[]>([]);
    private bonSortieTableData = new BehaviorSubject<BonSortie[]>([]);
    private tableData = new BehaviorSubject<Quote[]>([]);
    /** Documents type AVOIR (vente) — même endpoint statut/{sid}/{typeId} que la facture. */
    private avoirTableData = new BehaviorSubject<Facture[]>([]);
    /** id type document AVOIR résolu via GET /api/type-documents */
    private avoirTypeDocumentId: string | null = null;

    // Observables
    quoteTableData$ = this.quoteTableData.asObservable();
    orderTableData$ = this.orderTableData.asObservable();
    bonLivraisonTableData$ = this.bonLivraisonTableData.asObservable();
    factureTableData$ = this.factureTableData.asObservable();
    bonSortieTableData$ = this.bonSortieTableData.asObservable();
    tableData$ = this.tableData.asObservable();
    avoirTableData$ = this.avoirTableData.asObservable();

    constructor(
        private http: HttpClient,
        private toastr: ToastrService,
        private router: Router,
        private documentService: DocumentService,
        private listLoading: DocumentListLoadingService
    ) {
        // Les dates sont restaurées par SharedComponent selon le type de document.
        this.initializeStationAndLoadData();
    }

    private initializeStationAndLoadData(): void {
        this.loadUserData();

        if (!this.stationId || this.stationId === 0) {
            this.toastr.error('Aucune station trouvée. Veuillez créer une station.', 'Erreur');
            this.router.navigate(['/create-station']);
            return;
        }
        this.loadAllDataSafely();
    }
    private loadAllDataSafely() {
        const initialType = 'devis';
        this.loadByTypeAndStatus(initialType, 'Tous');
    }

    private loadUserData(): void {
        try {
            const userJson = localStorage.getItem('currentUser');
            if (!userJson) {
                this.stationId = 0;
                return;
            }

            const user = JSON.parse(userJson);
            const stationId = getEnterpriseStationIdFromUser(user);

            if (!stationId) {
                this.stationId = 0;
                return;
            }

            this.stationId = stationId;
        } catch (error) {
            this.toastr.error('Erreur lors de la récupération des données utilisateur.', 'Erreur');
            this.stationId = 0;
        }
    }

    private loadAllData() {
        this.loadByTypeAndStatus('devis', 'Tous');
        this.loadByTypeAndStatus('commande-client', 'Tous');
        this.loadByTypeAndStatus('bon-livraison', 'Tous');
        this.loadByTypeAndStatus('facture', 'Tous');
        this.loadByTypeAndStatus('bon-sortie', 'Tous');
        this.loadByTypeAndStatus('commande-fournisseur', 'Tous');
        this.loadByTypeAndStatus('bon-reception', 'Tous');
        this.loadByTypeAndStatus('facture-fournisseur', 'Tous');
    }


    loadByTypeAndStatus(type: string, status: string, dateFrom?: string, dateTo?: string) {
        if (dateFrom !== undefined || dateTo !== undefined) {
            this.setDateRangeFilter(dateFrom, dateTo);
        }
        if (type === 'avoir' && !this.avoirTypeDocumentId) {
            this.listLoading.start();
            this.resolveAvoirTypeDocumentId().pipe(
                mergeMap((id) => {
                    if (!id) {
                        this.updateData('avoir', []);
                        return EMPTY;
                    }
                    this.avoirTypeDocumentId = String(id);
                    this.loadByTypeAndStatus('avoir', status, dateFrom, dateTo);
                    return EMPTY;
                }),
                catchError(() => {
                    this.updateData('avoir', []);
                    return EMPTY;
                }),
                finalize(() => this.listLoading.stop())
            ).subscribe();
            return;
        }

        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            this.updateData(type, []);
            return;
        }

        this.listLoading.start();
        const statusId = this.statusMap[status] ?? '0';
        if (status === 'Tous') {
            this.loadAllStatusesForType(type, dateFrom, dateTo);
            return;
        }
        if (type === 'facture') {
            this.refreshFactureStatusTotals(dateFrom, dateTo);
        }
        let params = new HttpParams()
            .set('page', '1')
            .set('limit', '50')
            .set('station_id', this.stationId.toString())
            .set('light', '1');
        params = this.appendDateParams(params, dateFrom, dateTo);

        const url = `${this.apiBase}/${statusId}/${typeId}`;


        this.http.get<any>(url, {params}).pipe(
            tap(response => {

                const docs = response?.data?.documents || [];
                if (!Array.isArray(docs)) {
                    this.updateData(type, []);
                    return;
                }

                if (type === 'facture') {
                    const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                    this.factureTotalItemsByStatus[status] = Number(totalItems) || 0;
                    if (status === 'Annulé') {
                        this.factureTotalItemsByStatus['Annulée'] = Number(totalItems) || 0;
                    }
                    this.publishFactureTotals();
                }
                if (type === 'devis') {
                    const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                    this.quoteTotalItemsByStatus[status] = Number(totalItems) || 0;
                }

                const normalized = docs.map(item => this.normalizeItem(item, status));

                this.updateData(type, normalized);
            }),
            catchError(err => {
                this.updateData(type, []);
                return of([]);
            }),
            finalize(() => this.listLoading.stop())
        ).subscribe();
    }

    /** Résout l’id type « AVOIR » (sans fournisseur) depuis /api/type-documents. */
    private resolveAvoirTypeDocumentId(): Observable<number | null> {
        return this.documentService.getTypeDocuments().pipe(
            map((response: any) => {
                const list = this.extractTypeDocumentsList(response);
                for (const row of list) {
                    const titreNorm = this.normalizeAvoirTypeTitre(row?.titre || row?.nom || '');
                    const id = Number(row?.id ?? row?.type_document_id);
                    if (!id || Number.isNaN(id)) {
                        continue;
                    }
                    if (titreNorm === 'AVOIR' || (titreNorm.includes('AVOIR') && !titreNorm.includes('FOURNISSEUR'))) {
                        return id;
                    }
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    private extractTypeDocumentsList(response: any): any[] {
        if (Array.isArray(response)) {
            return response;
        }
        if (response?.data?.types && Array.isArray(response.data.types)) {
            return response.data.types;
        }
        if (Array.isArray(response?.data)) {
            return response.data;
        }
        if (response?.types && Array.isArray(response.types)) {
            return response.types;
        }
        const raw = response?.data ?? response;
        if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray((raw as any).types)) {
            return (raw as any).types;
        }
        return [];
    }

    private normalizeAvoirTypeTitre(s: string | null | undefined): string {
        return (s || '')
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    private getTypeIdForLoad(type: string): string | null {
        if (type === 'avoir') {
            return this.avoirTypeDocumentId;
        }
        return this.typeMap[type] || null;
    }

    /** Statuts à agréger pour l’onglet « Tous » (uniquement ceux du type courant). */
    private getStatusKeysForType(type: string): string[] {
        const keys = this.documentStatusesByType[type];
        if (keys?.length) {
            return keys;
        }
        if (type === 'avoir') {
            return ['Brouillon', 'Non utilisé', 'Partiellement utilisé', 'Utilisé', 'Annulé'];
        }
        return Object.keys(this.statusMap).filter((s) => s !== 'Tous');
    }

    filterAvoirByStatus(status: string) {
        this.loadByTypeAndStatus('avoir', status);
    }

    filterAvoirByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'avoir');
    }

    filterByStatus(status: string, type: string = 'devis', dateFrom?: string, dateTo?: string) {
        this.loadByTypeAndStatus(type, status, dateFrom, dateTo);
    }

    /** Mémorise la plage calendrier pour les prochains chargements API. */
    setDateRangeFilter(dateFrom?: string, dateTo?: string): void {
        this.activeDateFrom = dateFrom?.trim() || undefined;
        this.activeDateTo = dateTo?.trim() || undefined;
    }

    getActiveDateRange(): { from?: string; to?: string } {
        return {
            from: this.activeDateFrom,
            to: this.activeDateTo ?? this.activeDateFrom,
        };
    }

    private appendDateParams(params: HttpParams, dateFrom?: string, dateTo?: string): HttpParams {
        const from = (dateFrom ?? this.activeDateFrom)?.trim();
        const to = (dateTo ?? this.activeDateTo ?? from)?.trim();
        if (from) {
            params = params.set('date_from', from);
        }
        if (to) {
            params = params.set('date_to', to);
        }
        return params;
    }

    filterByDateRange(start: string, end: string, type: string = 'devis') {
        this.setDateRangeFilter(start, end);
    }

    private updateData(type: string, data: any[], append = false) {
        let finalData = data;

        if (append) {
            const current = this.getCurrentData(type);
            finalData = [...current, ...finalData]; 
        }

        switch (type) {
            case 'devis':
                this.quoteTableData.next(finalData);
                this.tableData.next(finalData);
                break;
            case 'commande-client':
                this.orderTableData.next(finalData);
                break;
            case 'bon-livraison':
                this.bonLivraisonTableData.next(finalData);
                break;
            case 'facture':
                this.factureTableData.next(finalData);
                break;
            case 'bon-sortie':
                this.bonSortieTableData.next(finalData);
                break;
            case 'avoir':
                this.avoirTableData.next(finalData);
                break;
        }
    }

    private getCurrentData(type: string): any[] {
        switch (type) {
            case 'devis':
                return this.quoteTableData.getValue();
            case 'commande-client':
                return this.orderTableData.getValue();
            case 'bon-livraison':
                return this.bonLivraisonTableData.getValue();
            case 'facture':
                return this.factureTableData.getValue();
            case 'bon-sortie':
                return this.bonSortieTableData.getValue();
            case 'avoir':
                return this.avoirTableData.getValue();
            default:
                return [];
        }
    }

    // GETTERS
    getQuoteTableData(): Quote[] {
        return this.quoteTableData.getValue();
    }

    getOrderTableData(): ClientOrder[] {
        return this.orderTableData.getValue();
    }

    getBonLivraisonTableData(): BonLivraison[] {
        return this.bonLivraisonTableData.getValue();
    }

    getFactureTableData(): Facture[] {
        return this.factureTableData.getValue();
    }

    getAvoirTableData(): Facture[] {
        return this.avoirTableData.getValue();
    }

    getBonSortieTableData(): BonSortie[] {
        return this.bonSortieTableData.getValue();
    }


    getTableData(): Quote[] {
        return this.tableData.getValue();
    }

    private getDataByType(type: string): any[] {
        switch (type) {
            case 'devis':
                return this.quoteTableData.getValue();
            case 'commande-client':
                return this.orderTableData.getValue();
            case 'bon-livraison':
                return this.bonLivraisonTableData.getValue();
            case 'facture':
                return this.factureTableData.getValue();
            case 'bon-sortie':
                return this.bonSortieTableData.getValue();
            case 'avoir':
                return this.avoirTableData.getValue();
            default:
                return [];
        }
    }

    private updateDataByType(type: string, data: any[]) {
        switch (type) {
            case 'devis':
                this.quoteTableData.next(data);
                this.tableData.next(data);
                break;
            case 'commande-client':
                this.orderTableData.next(data);
                break;
            case 'bon-livraison':
                this.bonLivraisonTableData.next(data);
                break;
            case 'facture':
                this.factureTableData.next(data);
                break;
            case 'bon-sortie':
                this.bonSortieTableData.next(data);
                break;
            case 'avoir':
                this.avoirTableData.next(data);
                break;
        }
    }

    filterQuotesByStatus(status: string, dateFrom?: string, dateTo?: string) {
        this.filterByStatus(status, 'devis', dateFrom, dateTo);
    }

    filterOrdersByStatus(status: string, dateFrom?: string, dateTo?: string) {
        this.filterByStatus(status, 'commande-client', dateFrom, dateTo);
    }

    filterBonLivraisonByStatus(status: string, dateFrom?: string, dateTo?: string) {
        this.filterByStatus(status, 'bon-livraison', dateFrom, dateTo);
    }

    filterFactureByStatus(status: string, dateFrom?: string, dateTo?: string) {
        this.filterByStatus(status, 'facture', dateFrom, dateTo);
    }

    filterBonSortieByStatus(status: string, dateFrom?: string, dateTo?: string) {
        this.filterByStatus(status, 'bon-sortie', dateFrom, dateTo);
    }

    filterQuotesByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'devis');
    }

    filterOrdersByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'commande-client');
    }

    filterBonLivraisonByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'bon-livraison');
    }

    filterFactureByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'facture');
    }

    filterBonSortieByDateRange(start: string, end: string) {
        this.filterByDateRange(start, end, 'bon-sortie');
    }


    getQuoteStatusCount(status: string): number {
        return this.getStatusCount(this.quoteTableData.getValue(), status);
    }

    getOrderStatusCount(status: string): number {
        return this.getStatusCount(this.orderTableData.getValue(), status);
    }

    getBonLivraisonStatusCount(status: string): number {
        return this.getStatusCount(this.bonLivraisonTableData.getValue(), status);
    }

    getFactureStatusCount(status: string): number {
        return this.getStatusCount(this.factureTableData.getValue(), status);
    }

    /** Nombre total renvoyé par l'API pour la facture (total_items). Retourne 0 si pas encore chargé. */
    getFactureTotalItems(status: string): number {
        const keys = [status];
        if (status === 'Annulé') { keys.push('Annulée'); }
        if (status === 'Annulée') { keys.push('Annulé'); }
        if (status === 'Non payé') { keys.push('Non Payé'); }
        for (const k of keys) {
            const n = this.factureTotalItemsByStatus[k];
            if (typeof n === 'number') { return n; }
        }
        return 0;
    }

    /** total_items de l'API api/documents/statut/{statusId}/7 pour le devis. */
    getQuoteTotalItems(status: string): number {
        const n = this.quoteTotalItemsByStatus[status];
        return typeof n === 'number' ? n : 0;
    }

    getBonSortieStatusCount(status: string): number {
        return this.getStatusCount(this.bonSortieTableData.getValue(), status);
    }

    private getStatusCount(data: any[], status: string): number {
        const normalized = this.normalizeStatus(status);
        return normalized === 'Tous' ? data.length : data.filter(i => i.status === normalized).length;
    }

    private normalizeStatus(status: string): string {
        const map: { [key: string]: string } = {
            'tous': 'Tous',
            'brouillon': 'Brouillon',
            'cree': 'Crée',
            'créé': 'Crée',
            'annule': 'Annulé',
            'annulé': 'Annulé',
            'valide': 'Valide',
            'paye': 'Payé',
            'payé': 'Payé',
            'nonpaye': 'Non payé',
            'non paye': 'Non payé',
            'non_payé': 'Non payé',
            'non_paye': 'Non payé',
            'partiellement paye': 'Partiellement payé',
            'partiellement payé': 'Partiellement payé',
            'livré': 'Livré',
            'livre': 'Livré',
            'accepte': 'Accepté',
            'accepté': 'Accepté',
            'negociation': 'Négociation',
            'négociation': 'Négociation',
            'recu': 'Reçu',
            'reçu': 'Reçu'
        };

        const lower = status
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ');
        return map[lower] || map[status.toLowerCase().trim()] || status;
    }

    setStationId(id: string | number): void {
        const parsedId = Number(id);
        if (isNaN(parsedId) || parsedId <= 0) {
            this.stationId = 0;
            return;
        }
        this.stationId = parsedId;
        this.loadAllData();
    }


    private loadAllStatusesForType(type: string, dateFrom?: string, dateTo?: string) {
        const statusKeys = this.getStatusKeysForType(type);

        const requests = statusKeys.map(statut => {
            const statusId = this.statusMap[statut];
            return this.fetchFromApiWithTotal(type, statusId, statut, dateFrom, dateTo);
        });

        forkJoin(requests).pipe(
            map((results: { documents: any[]; total_items: number }[]) => {
                const allDocs: any[] = [];
                let totalTous = 0;
                results.forEach((r, i) => {
                    allDocs.push(...r.documents);
                    totalTous += r.total_items;
                    if (type === 'facture' && statusKeys[i]) {
                        this.factureTotalItemsByStatus[statusKeys[i]] = r.total_items;
                        if (statusKeys[i] === 'Annulé') {
                            this.factureTotalItemsByStatus['Annulée'] = r.total_items;
                        }
                    }
                    if (type === 'devis' && statusKeys[i]) {
                        this.quoteTotalItemsByStatus[statusKeys[i]] = r.total_items;
                    }
                });
                if (type === 'facture') {
                    this.factureTotalItemsByStatus['Tous'] = totalTous;
                    this.publishFactureTotals();
                }
                if (type === 'devis') {
                    this.quoteTotalItemsByStatus['Tous'] = totalTous;
                }
                return allDocs.sort((a, b) => {
                    if (a.id && b.id) return b.id - a.id;
                    if (a.date && b.date) return new Date(b.date).getTime() - new Date(a.date).getTime();
                    return 0;
                });
            }),
            catchError(err => {
                console.error('Erreur lors du chargement de tous les statuts:', err);
                return of([]);
            }),
            finalize(() => this.listLoading.stop())
        ).subscribe({
            next: (normalizedDocs) => {
                this.updateData(type, normalizedDocs, false);
            },
            error: () => {
                this.updateData(type, [], false);
            }
        });
    }

    /** Compteurs onglets facture selon filtre date (limit=1, total_items seulement). */
    private refreshFactureStatusTotals(dateFrom?: string, dateTo?: string): void {
        const statusKeys = this.getStatusKeysForType('facture');
        const requests = statusKeys.map((statut) => {
            const statusId = this.statusMap[statut];
            const typeId = this.getTypeIdForLoad('facture');
            if (!typeId || !statusId) {
                return of({ statut, total_items: 0 });
            }
            let params = new HttpParams()
                .set('page', '1')
                .set('limit', '1')
                .set('station_id', String(this.stationId));
            params = this.appendDateParams(params, dateFrom, dateTo);
            const url = `${this.apiBase}/${statusId}/${typeId}`;
            return this.http.get<any>(url, { params }).pipe(
                map((response) => {
                    const totalItems =
                        response?.data?.total_items ??
                        response?.data?.pagination?.total_items ??
                        (response?.data?.documents || []).length;
                    return { statut, total_items: Number(totalItems) || 0 };
                }),
                catchError(() => of({ statut, total_items: 0 }))
            );
        });

        forkJoin(requests).subscribe({
            next: (results) => {
                let totalTous = 0;
                results.forEach((r) => {
                    this.factureTotalItemsByStatus[r.statut] = r.total_items;
                    if (r.statut === 'Annulé') {
                        this.factureTotalItemsByStatus['Annulée'] = r.total_items;
                    }
                    totalTous += r.total_items;
                });
                this.factureTotalItemsByStatus['Tous'] = totalTous;
                this.publishFactureTotals();
            }
        });
    }

    private publishFactureTotals(): void {
        this.factureTotalsSubject.next({ ...this.factureTotalItemsByStatus });
    }

    private fetchFromApi(type: string, statusId: string): Observable<any[]> {
        return this.fetchFromApiWithTotal(type, statusId, '').pipe(
            map(r => r.documents)
        );
    }

    private fetchFromApiWithTotal(
        type: string,
        statusId: string,
        _statusName: string,
        dateFrom?: string,
        dateTo?: string
    ): Observable<{ documents: any[]; total_items: number }> {
        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            return of({ documents: [], total_items: 0 });
        }
        let params = new HttpParams()
            .set('page', '1')
            .set('limit', '100')
            .set('station_id', this.stationId)
            .set('light', '1');
        params = this.appendDateParams(params, dateFrom, dateTo);
        const url = `${this.apiBase}/${statusId}/${typeId}`;
        return this.http.get<any>(url, {params}).pipe(
            map(response => {
                const docs = response?.data?.documents || [];
                const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                return {
                    documents: docs.map((item: any) => this.normalizeItem(item, _statusName || undefined)),
                    total_items: Number(totalItems) || 0
                };
            }),
            catchError(() => of({ documents: [], total_items: 0 }))
        );
    }

    private resolveListReference(item: any): string {
        const typeTitre = String(item?.typeDocument?.titre || item?.type_document?.titre || '').toUpperCase().trim();
        let ref = String(item?.ref_externe || '').trim();
        if (ref && /^FAC(?=[-_]|$)/i.test(ref) && typeTitre.includes('AVOIR')) {
            const prefix = typeTitre.includes('FOURNISSEUR') ? 'AF' : 'AVR';
            ref = ref.replace(/^FAC/i, prefix);
        }
        return ref || (item?.id != null ? `REF-${item.id}` : '');
    }

    private normalizeItem(item: any, fallbackStatus?: string): any {
        const typeTitre = (item.typeDocument?.titre || '').toString().toUpperCase();
        const isAchat = [
            'COMMANDE FOURNISSEUR',
            'BON RECEPTION',
            'FACTURE FOURNISSEUR',
            'RETOUR FOURNISSEUR'
        ].includes(typeTitre);
        const clientName = isAchat
            ? this.resolveFournisseurDisplayName(item)
            : this.resolveClientDisplayName(item);

        const matricule = isAchat
            ? item.fornisseur_data?.numero_identification_fiscale ||
              item.fournisseur_data?.numero_identification_fiscale ||
              item.fornisseurs?.[0]?.numero_identification_fiscale ||
              '--'
            : item.client_data?.identifiant_unique ||
              item.client_data?.matricule ||
              item.clients?.[0]?.identifiant_unique ||
              '--';

        const email = item.client_data?.email || item.clients?.[0]?.email || item.fornisseur_data?.email || null;
        const telephones = item.client_data?.telephones || item.fornisseur_data?.telephones || [];
        const codeClient =
            item.clients?.[0]?.code_client ||
            item.client_data?.code_client ||
            item.clients?.[0]?.identifiant_unique ||
            item.client_data?.identifiant_unique ||
            null;
        const nif =
            item.clients?.[0]?.numero_identification_fiscale ||
            item.client_data?.numero_identification_fiscale ||
            null;
        const codeEtablissement =
            item.code_etablissement ||
            item.etablissement?.code_etablissement ||
            item.etablissement?.code ||
            (item.etablissement?.id != null
                ? String(item.etablissement.id).padStart(5, '0')
                : null);
        const codeEntrepot =
            item.code_entrepot ||
            item.entrepot?.code_entrepot ||
            item.entrepot?.code ||
            null;

        let status = (item.statut?.titre || '').toString().trim();
        // Préférer le libellé canonique via statut_id (évite « Non pay├® » / titres corrompus).
        const sid = Number(item.statut_id ?? item.statut?.id);
        const byId: Record<number, string> = {
            1: 'Brouillon',
            3: 'Accepté',
            5: 'Payé',
            6: 'Annulé',
            8: 'Négociation',
            9: 'Crée',
            10: 'Valide',
            11: 'Livré',
            12: 'Non payé',
            13: 'Fermé',
            14: 'Reçu',
            15: 'Partiellement payé',
            16: 'En cours',
            17: 'Transféré',
            18: 'Utilisé',
            19: 'Non utilisé',
            20: 'Partiellement utilisé',
        };
        if (Number.isFinite(sid) && byId[sid]) {
            status = byId[sid];
        } else if (status) {
            // Répare UTF-8 mal lu (ex. Non pay├® → Non payé)
            if (/[├ÂÃ]/.test(status)) {
                try {
                    const bytes = new Uint8Array(status.length);
                    for (let i = 0; i < status.length; i++) {
                        bytes[i] = status.charCodeAt(i) & 0xff;
                    }
                    const decoded = new TextDecoder('utf-8').decode(bytes);
                    if (decoded && !decoded.includes('\uFFFD')) {
                        status = decoded.trim();
                    }
                } catch {
                    status = status.replace(/pay├®/gi, 'payé').replace(/├®/g, 'é');
                }
            }
            const key = status
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim()
                .replace(/\s+/g, ' ');
            const titleMap: Record<string, string> = {
                brouillon: 'Brouillon',
                paye: 'Payé',
                payee: 'Payé',
                'non paye': 'Non payé',
                'partiellement paye': 'Partiellement payé',
                annule: 'Annulé',
                annulee: 'Annulé',
            };
            status = titleMap[key] || status;
        } else {
            status = fallbackStatus || '';
        }
        if (!status) {
            status = (Number.isFinite(sid) && byId[sid]) || 'Inconnu';
        }

        return {
            id: item.id,
            reference: this.resolveListReference(item),
            clientName,
            date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
            revenue: item.total_ttc ? `${parseFloat(item.total_ttc).toFixed(3)} TND` : '0.000 TND',
            totalHt: item.total_ht != null ? `${parseFloat(item.total_ht).toFixed(3)} TND` : '0.000 TND',
            totalTva: item.total_tva != null ? `${parseFloat(item.total_tva).toFixed(3)} TND` : '0.000 TND',
            timbreFiscal:
                item.timbre_fiscal != null ? `${parseFloat(item.timbre_fiscal).toFixed(3)} TND` : '0.000 TND',
            netPayer: item.net_payer != null ? `${parseFloat(item.net_payer).toFixed(3)} TND` : '0.000 TND',
            note: item.note != null && String(item.note).trim() !== '' ? String(item.note).trim() : '—',
            createdAt: item.created_at || item.createdAt || null,
            matricule,
            status,
            email,
            codeClient,
            nif,
            codeEtablissement,
            codeEntrepot,
            phoneNumbers: Array.isArray(telephones) ? telephones : [],
            station_id: item.station_id || null,
            user_id: item.user_id || null,
            fullData: item
        };
    }

    /** Affiche l’entreprise (pro / négoce) ou nom+prénom (particulier) — préfère un libellé non-email. */
    private resolveClientDisplayName(item: any): string {
        const sources = [
            item?.clients?.[0],
            item?.client_data,
            item?.client
        ].filter(Boolean);

        let fallback = '';
        for (const c of sources) {
            const type = String(c.type || '')
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
            const isPro =
                type === 'professionnel' || type === 'negoce' || type === 'patente' || type === 'entreprise';
            const isParticulier =
                type === 'individuel' || type === 'particulier' || type === 'materiel';
            const entreprise = String(c.nom_entreprise || c.raison_social || '').trim();
            const nomPrenom = String(c.nom_prenom || '').trim();
            const nom = String(c.nom || '').trim();
            const prenom = String(c.prenom || '').trim();
            const composed = [prenom, nom].filter(Boolean).join(' ').trim();
            const nomClient = String(c.nom_client || c.nomClient || '').trim();

            const candidates = isPro
                ? [entreprise, nomClient, nomPrenom, composed]
                : isParticulier || type
                  ? [nomPrenom, composed, nomClient, entreprise]
                  : [entreprise, nomPrenom, composed, nomClient];

            for (const raw of candidates) {
                const label = String(raw || '').trim();
                if (!label) {
                    continue;
                }
                if (!this.looksLikeEmail(label)) {
                    return label;
                }
                if (!fallback) {
                    fallback = label;
                }
            }
        }
        return fallback || '--';
    }

    private resolveFournisseurDisplayName(item: any): string {
        const name =
            item?.fornisseur_data?.nom_entreprise ||
            item?.fournisseur_data?.nom_entreprise ||
            item?.fornisseurs?.[0]?.nom_entreprise ||
            item?.fournisseur?.nom_entreprise ||
            '';
        return String(name).trim() || '--';
    }

    private looksLikeEmail(value: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    }
}
